/**
 * Buyer Selection Card Component
 * Handles buyer search, selection, and user management for chat creation
 */

import type {
  BuyerSelectOption,
  CreateChatFormData,
  BuyerUserSelectOption,
} from '@choco-admin/types';
import {
  Button,
  Card,
  CardBody,
  Autocomplete,
  AutocompleteItem,
} from '@heroui/react';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiUser, FiUsers, FiX } from 'react-icons/fi';

import { Pill } from '../../../../../../components/Pill';
import { useGetBuyerUsers } from '../../../../hooks/useGetBuyerUsers';
import { useSearchBuyersForChat } from '../../../../hooks/useSearchBuyersForChat';

interface BuyerSelectionCardProps {
  onBuyerSelect: (buyer: BuyerSelectOption) => void;
  onBuyerUserToggle: (user: BuyerUserSelectOption) => void;
  onRemoveBuyerUser: (userId: string) => void;
}

export const BuyerSelectionCard: React.FC<BuyerSelectionCardProps> = ({
  onBuyerSelect,
  onBuyerUserToggle,
  onRemoveBuyerUser,
}) => {
  const { control, watch, setValue } = useFormContext<CreateChatFormData>();
  const formData = watch();

  // Data hooks
  const {
    buyers,
    isLoading: buyersLoading,
    searchBuyers,
  } = useSearchBuyersForChat();

  const {
    buyerUsers,
    loading: buyerUsersLoading,
    getBuyerUsers,
  } = useGetBuyerUsers();

  // Fetch buyer users when a buyer is selected
  useEffect(() => {
    if (formData.buyer?.value) {
      getBuyerUsers(formData.buyer.value);
    }
  }, [formData.buyer?.value, getBuyerUsers]);

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <FiUser className="text-primary" />
          <h4 className="font-medium">Buyer *</h4>
        </div>

        <Controller
          name="buyer"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              placeholder="Type to search buyers..."
              aria-label="Search and select buyer"
              isLoading={buyersLoading}
              errorMessage={fieldState.error?.message}
              isInvalid={!!fieldState.error}
              selectedKey={field.value?.value || null}
              items={buyers}
              onInputChange={(value) => {
                if (value.length > 2) {
                  searchBuyers(value);
                } else if (value.length === 0) {
                  // Clear results when input is empty
                  // This helps with the re-rendering issue
                }
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedBuyer = buyers.find(
                    (buyer: BuyerSelectOption) => buyer.value === key
                  );
                  if (selectedBuyer) {
                    onBuyerSelect(selectedBuyer);
                  }
                } else {
                  setValue('buyer', null);
                  setValue('buyerUsers', []);
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(buyer: BuyerSelectOption) => (
                <AutocompleteItem
                  key={buyer.value}
                  textValue={buyer.label}
                  className="py-3 border-b border-divider last:border-b-0"
                >
                  <div className="flex flex-col py-1">
                    <span className="text-small font-medium text-foreground">
                      {buyer.label}
                    </span>
                    {buyer.metaData?.fullAddress && (
                      <span className="text-tiny text-foreground-600 mt-1">
                        {buyer.metaData.fullAddress}
                      </span>
                    )}
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>
          )}
        />

        {/* Selected Buyer */}
        {formData.buyer && (
          <Card className="bg-primary-50 border-primary-200">
            <CardBody className="p-3">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-primary-800 break-words">
                    {formData.buyer.label}
                  </div>
                  {formData.buyer.metaData?.fullAddress && (
                    <div className="text-xs text-primary-600 mt-1 break-words">
                      {formData.buyer.metaData.fullAddress}
                    </div>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="flex-shrink-0"
                  onPress={() => {
                    setValue('buyer', null);
                    setValue('buyerUsers', []);
                  }}
                >
                  <FiX />
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Buyer Users */}
        {formData.buyer && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FiUsers className="text-default-500" />
              <span className="text-sm font-medium">Buyer Users</span>
              <span className="text-xs text-foreground-600">(optional)</span>
            </div>

            <Autocomplete
              placeholder="Search buyer users..."
              aria-label="Search and select buyer users"
              isLoading={buyerUsersLoading}
              selectedKey={null}
              items={buyerUsers}
              onInputChange={(value) => {
                // Buyer users are already loaded, so we can search through them locally
                // or implement server-side search if needed in the future
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedUser = buyerUsers.find(
                    (user: BuyerUserSelectOption) => user.value === key
                  );
                  if (selectedUser) {
                    onBuyerUserToggle(selectedUser);
                  }
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(user: BuyerUserSelectOption) => (
                <AutocompleteItem
                  key={user.value}
                  textValue={user.label}
                  className="py-3 border-b border-divider last:border-b-0"
                >
                  <div className="flex flex-col py-1">
                    <span className="text-small font-medium text-foreground">
                      {user.label}
                    </span>
                    {user.metaData?.email && (
                      <span className="text-tiny text-foreground-600 mt-1">
                        {user.metaData.email}
                      </span>
                    )}
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>

            {/* Selected Buyer Users */}
            {formData.buyerUsers && formData.buyerUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.buyerUsers.map((user) => (
                  <Pill
                    key={user.value}
                    variant="primary"
                    size="sm"
                    className="max-w-full"
                    onClick={() => onRemoveBuyerUser(user.value)}
                  >
                    <div className="flex items-center gap-1">
                      <span className="break-words truncate">{user.label}</span>
                      <FiX className="text-xs cursor-pointer" />
                    </div>
                  </Pill>
                ))}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
